import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, BookOpen, Code2, KeyRound, Send } from 'lucide-react';

const docsSamples = {
  curl: `curl -X POST "http://localhost:4000/api/v1.0/templates/send" \\
  -H "Authorization: Bearer <API_TOKEN>" \\
  -H "x-client-url: https://client.example.com" \\
  -H "Content-Type: application/json" \\
  -d '{
    "templateName": "order_update",
    "languageCode": "en_US",
    "customers": [
      { "phoneNumber": "919876543210", "name": "John Doe" }
    ],
    "components": []
  }'`,
  single: `{
  "templateName": "order_update",
  "languageCode": "en_US",
  "phoneNumber": "919876543210",
  "components": [
    {
      "type": "body",
      "parameters": [
        { "type": "text", "text": "John" },
        { "type": "text", "text": "ORD-1001" }
      ]
    }
  ]
}`,
  bulk: `{
  "templateName": "order_update",
  "languageCode": "en_US",
  "customers": [
    { "phoneNumber": "919876543210", "name": "John Doe", "clientReference": "CUS-1" },
    { "phoneNumber": "919812345678", "name": "Jane Doe", "clientReference": "CUS-2" }
  ],
  "components": [
    {
      "type": "body",
      "parameters": [
        { "type": "text", "text": "Customer" },
        { "type": "text", "text": "ORD-1001" }
      ]
    }
  ]
}`,
  bodyOnly: `{
  "templateName": "appointment_reminder",
  "languageCode": "en_US",
  "phoneNumber": "919876543210",
  "components": [
    {
      "type": "body",
      "parameters": [
        { "type": "text", "text": "John" },
        { "type": "text", "text": "Dr. Arun" },
        { "type": "text", "text": "12 March 2026" },
        { "type": "text", "text": "10:30 AM" }
      ]
    }
  ]
}`,
  headerText: `{
  "templateName": "promo_header_text",
  "languageCode": "en_US",
  "phoneNumber": "919876543210",
  "components": [
    {
      "type": "header",
      "parameters": [
        { "type": "text", "text": "March Offer" }
      ]
    },
    {
      "type": "body",
      "parameters": [
        { "type": "text", "text": "John" }
      ]
    }
  ]
}`,
  headerMedia: `{
  "templateName": "invoice_document",
  "languageCode": "en_US",
  "phoneNumber": "919876543210",
  "components": [
    {
      "type": "header",
      "parameters": [
        {
          "type": "document",
          "document": {
            "link": "https://client.example.com/files/invoice-1001.pdf",
            "filename": "invoice-1001.pdf"
          }
        }
      ]
    },
    {
      "type": "body",
      "parameters": [
        { "type": "text", "text": "John" },
        { "type": "text", "text": "INV-1001" }
      ]
    }
  ]
}`,
  buttonUrl: `{
  "templateName": "payment_link_template",
  "languageCode": "en_US",
  "phoneNumber": "919876543210",
  "components": [
    {
      "type": "body",
      "parameters": [
        { "type": "text", "text": "John" }
      ]
    },
    {
      "type": "button",
      "sub_type": "url",
      "index": "0",
      "parameters": [
        { "type": "text", "text": "pay-now-1001" }
      ]
    }
  ]
}`,
};

const CodeBlock = ({ label, value, rows = 12 }) => (
  <Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
      {label}
    </Typography>
    <TextField
      fullWidth
      multiline
      minRows={rows}
      value={value}
      InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', alignItems: 'flex-start' } }}
    />
  </Box>
);

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)' }}>
    <CardContent>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: 'rgba(34,197,94,0.12)',
            color: '#15803d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={18} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const TemplateApiDocsPage = () => {
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1320, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} mb={3}>
        <Button variant="outlined" startIcon={<ArrowLeft size={18} />} href="/settings/api-token">
          Back To Tokens
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Template API Docs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Clean integration docs for client developers using the template send API.
          </Typography>
        </Box>
      </Stack>

      <Card
        sx={{
          mb: 3,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(15,118,110,0.10) 0%, rgba(34,197,94,0.08) 45%, rgba(255,255,255,1) 100%)',
          boxShadow: '0 18px 50px rgba(15, 23, 42, 0.06)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Endpoint" value="POST http://localhost:4000/api/v1.0/templates/send" InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Auth" value="Bearer token" InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Whitelist" value="x-client-url must match token URL" InputProps={{ readOnly: true }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mb: 3 }}>
        One token supports one whitelisted URL. If a client needs a second URL, create a second token. Only approved templates can be sent.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <SectionCard
            icon={BookOpen}
            title="How To Call"
            subtitle="Required headers and the standard cURL request."
          >
            <Stack spacing={1} mb={2}>
              <Typography variant="body2" color="text.secondary">
                Required headers: `Authorization: Bearer &lt;API_TOKEN&gt;`, `x-client-url`, `Content-Type: application/json`
              </Typography>
              <Typography variant="body2" color="text.secondary">
                `templateName` must match an approved template in your ChattyBo account.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use `phoneNumber` for one customer or `customers` for bulk sending.
              </Typography>
            </Stack>
            <CodeBlock label="cURL Example" value={docsSamples.curl} rows={10} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <SectionCard
            icon={KeyRound}
            title="Rules"
            subtitle="What the backend validates before sending."
          >
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                The token must be valid and active in API Token Management.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The `x-client-url` header must match the single whitelist URL stored on that token.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                `components` must match the approved Meta template structure.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All sends are recorded in ChattyBo with template name, customer number, status, date/time, and client token metadata.
              </Typography>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard icon={Send} title="Single Customer Payload" subtitle="Send one template message to one customer.">
            <CodeBlock label="Single" value={docsSamples.single} rows={12} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard icon={Send} title="Bulk Customer Payload" subtitle="Send the same template to multiple customers.">
            <CodeBlock label="Bulk" value={docsSamples.bulk} rows={12} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard icon={Code2} title="Body Variable Template" subtitle="Typical body placeholder mapping.">
            <CodeBlock label="Body Variables" value={docsSamples.bodyOnly} rows={14} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard icon={Code2} title="Header Text Template" subtitle="Header text plus body variables.">
            <CodeBlock label="Header Text" value={docsSamples.headerText} rows={14} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard icon={Code2} title="Header Media Template" subtitle="Document/image/video style header parameter payload.">
            <CodeBlock label="Header Media" value={docsSamples.headerMedia} rows={18} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard icon={Code2} title="Button URL Template" subtitle="Dynamic URL button template payload.">
            <CodeBlock label="Button URL" value={docsSamples.buttonUrl} rows={18} />
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard icon={BookOpen} title="Success Response" subtitle="What the client receives after the request is processed.">
            <CodeBlock
              label="Response"
              value={`{
  "success": true,
  "message": "Template message request processed successfully",
  "data": {
    "client": { "id": "token-id", "name": "Client", "url": "https://client.example.com" },
    "template": { "id": 12, "name": "order_update", "language": "en_US" },
    "totals": { "requested": 2, "sent": 2, "failed": 0 },
    "results": [
      {
        "phoneNumber": "919876543210",
        "customerId": 101,
        "templateId": 12,
        "templateName": "order_update",
        "status": "sent",
        "messageId": "wamid....",
        "error": null
      }
    ]
  }
}`}
              rows={14}
            />
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemplateApiDocsPage;
